import { useEffect } from 'react';
import { Configure } from 'react-instantsearch-hooks-web';
import Head from 'next/head';
import { capitalize } from 'lodash-es';

import { DynamicComponents, Error, FlatSheet } from '@/templates';

import { InstantSearch } from '@/components/algolia';
import { flatName, flatPath, zonePath } from '@/components/algolia/utils';
import { getPage } from '@/graphql/queries/getPage';
import { getProperties } from '@/graphql/queries/getProperties';
import { getProperty } from '@/graphql/queries/getProperty';
import { routes } from '@/lib';
import { flatPropertiesTransformer, gtmPush } from '@/utils';

import type { Apartment, SingleFamilyResidence, WithContext } from 'schema-dts';

const FlatDetail = ({ errorCode, page, property, search }: any) => {
  useEffect(() => {
    gtmPush({
      event: 'view_item',
      currency: 'EUR',
      item_type: capitalize(property?.attributes?.ventaAlquiler),
      items: [
        {
          item_id: property?.attributes?.codigoUnidadAlquiler,
          item_name: property?.attributes?.nombreUE,
          currency: 'EUR',
          discount: '',
          item_list_id: zonePath(
            property?.attributes,
            property?.attributes?.ventaAlquiler
          ),
          item_list_name: `${property?.attributes?.provincia} > ${
            property?.attributes?.ciudad
          }${
            property?.attributes?.tipoZona?.infoAdicional
              ? ` > ${property.attributes.tipoZona.infoAdicional}`
              : ''
          }`.toLowerCase(),
          price: parseFloat(
            property?.attributes?.ventaAlquiler?.toLowerCase() === 'venta'
              ? property?.attributes?.importeVenta
              : property?.attributes?.importeRecibofinal
          ),
          city: property?.attributes?.ciudad,
          city_area: property?.attributes?.tipoZona?.infoAdicional,
          street: property?.attributes?.STREET,
          address: property?.attributes?.STREET,
          property_type: property?.attributes?.tipoDeUsoDescripcion,
          property_condition: property?.attributes?.estadoDescripcion,
          area: property?.attributes?.metrosComputablesUA ?? undefined,
          bedrooms_quantity: property?.attributes?.habitaciones,
          bathrooms_quantity: property?.attributes?.banos,
          elevator: !!property?.attributes?.Ascensores,
          communal_expenses: undefined,
          parking: property?.attributes?.hasGaraje, //
          furnishings: property?.attributes?.hasAmueblado, //
          floor: undefined,
          heating: property?.attributes?.instalacionCaldera,
        },
      ],
    });
  });

  const commonSchema: any = {
    name: property?.attributes?.nombreUE,
    description: property?.attributes?.otrosDatos?.argumentario,
    numberOfRooms:
      parseInt(property?.attributes?.habitaciones) +
      parseInt(property?.attributes?.banos),
    numberOfBathroomsTotal: property?.attributes?.banos,
    numberOfBedrooms: property?.attributes?.habitaciones,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ES',
      addressLocality: property?.attributes?.provincia,
      addressRegion: property?.attributes?.ciudad,
      streetAddress: property?.attributes?.nombreUE,
    },
    latitude: property?.attributes?.latitude,
    longitude: property?.attributes?.longitude,
  };

  const apartmentSchema: WithContext<Apartment> = {
    '@context': 'https://schema.org',
    '@type': 'Apartment',
    ...commonSchema,
  };

  const familyResidenceSchema: WithContext<SingleFamilyResidence> = {
    '@context': 'https://schema.org',
    '@type': 'SingleFamilyResidence',
    ...commonSchema,
  };

  return errorCode === 404 ? (
    <Error statusCode={errorCode} />
  ) : (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              property?.attributes?.ventaAlquiler === 'COMPRA'
                ? familyResidenceSchema
                : apartmentSchema
            ),
          }}
        />
      </Head>

      <FlatSheet property={property} />

      <InstantSearch
        indexName={
          process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? ''
        }
      >
        <Configure aroundLatLng={search} hitsPerPage={4} />
        {page?.attributes?.Content?.map(
          ({ __typename, ...props }: any, index: number) => (
            <DynamicComponents
              type={__typename}
              {...props}
              key={`${__typename}-${index}`}
              propertyId={property?.attributes?.codigoUnidadAlquiler}
            />
          )
        )}
      </InstantSearch>
    </>
  );
};

export async function getStaticProps({ params }: any) {
  const { pages, menusMenus, preFooter } = await getPage('/vivienda');
  const pathSplit =
    params?.id?.[0]?.split(routes.flats.flat('')).filter(Boolean)[0] ?? '';
  const id = pathSplit.match(/\d{4}-\d{8}-\d{8}/)?.[0].replaceAll('-', '/');
  const { zfmDisponiblidads } = await getProperty(id);
  const notFound = zfmDisponiblidads?.data.length == 0;
  const property = zfmDisponiblidads?.data?.[0] || {};
  const search = `${property?.attributes?.latitud || 0},${
    property?.attributes?.longitud || 0
  }`;

  if (property.attributes?.caracteristicas)
    property.attributes.caracteristicas = flatPropertiesTransformer(
      property.attributes.caracteristicas
    );

  return {
    props: {
      page: {
        title: flatName(zfmDisponiblidads?.data?.[0]?.attributes),
        metaDescription: flatName(zfmDisponiblidads?.data?.[0]?.attributes),
        metaKeywords: null,
        metaRobots: '',
        path: flatPath(zfmDisponiblidads?.data?.[0]?.attributes),
        theme: 'light',
        langcode: { id: 'es' },
        attributes: pages.data[0]?.attributes || null,
        menu: menusMenus?.data || [],
        preFooter: preFooter?.data || [],
      },
      search,
      property,
    },
    revalidate: process.env.NEXT_PUBLIC_REVALIDATE
      ? parseInt(process.env.NEXT_PUBLIC_REVALIDATE)
      : 1000,
    notFound,
  };
}

export async function getStaticPaths() {
  const { zfmDisponiblidads } = await getProperties();

  return {
    paths: zfmDisponiblidads.data.map((itm: any) => flatPath(itm.attributes)),
    fallback: 'blocking',
  };
}

export default FlatDetail;
